// data/lab/briefs.js
// ════════════════════════════════════════════════════════════════════
// LAB WEEKLY BRIEFS (Phase F14)
//
// Briefs are NOT findings. They are weekly cross-corpus syntheses —
// what moved across tracked shifts + findings + predictions this week.
// Lighter form: no falsifier, no pre-registered prediction, no
// cohort-completion resolution. Just a synthesis with cited evidence.
//
// Why this is a separate artifact: weekly cadence is incompatible with
// finding rigor (each finding takes weeks to author). Briefs get the
// reader something new every week without diluting the corpus.
//
// Schema:
//   {
//     id, slug, publishDate, weekOf, title, shortTitle, description,
//     author, citationHandle: 'lab:brief/<year>/week-<NN>',
//     visibility: 'private' | 'shared-link' | 'public-peek' | 'public-collab',
//     lifecycle: 'draft' | 'published' | 'archived',
//
//     lede,                            // 1-2 sentence anchor
//     moved: [{ shift?, finding?, headline, body }],
//                                      // what changed in tracked shifts
//                                      // / findings this week
//     watchMetrics: [{ metric, value, delta?, source?, asOf? }],
//                                      // current values for tracked metrics
//     predictionsOnDeck: [{ findingHandle, prediction, resolutionDate, daysOut? }],
//                                      // predictions resolving in next 90 days
//     predictionsResolved: [{ findingHandle, prediction, resolution, notes? }],
//                                      // predictions resolved this week
//     watchingNext: [string],          // 1-3 bullets — what to watch next week
//     citedFindings: [handle],         // auto-generatable from refs in moved
//     citedShifts: [shiftId],          // auto-generatable from refs in moved
//   }
//
// Authoring discipline: lede + moved + watchingNext are the human-
// authored parts. The rest is mostly structured cross-references the
// editor pre-populates from the corpus.
// ════════════════════════════════════════════════════════════════════

export const BRIEFS = [
  {
    id: '2026-w19',
    slug: '2026-week-19',
    publishDate: '2026-05-10',
    weekOf: 'May 5–11, 2026',
    title: 'Methodology week — Lab corpus crosses 7 findings, tectonic depth-pass lands',
    shortTitle: 'Week 19 — Corpus + depth pass',
    description: 'Internal-week brief. Lab finding count to 7 (with Israel-Haredi, Refugia template, Substrate-Symmetric joining the popdec trilogy). Tectonic shifts upgraded with charts + historical analog library.',
    author: 'Tim Mai',
    citationHandle: 'lab:brief/2026/week-19',
    visibility: 'public-peek',
    lifecycle: 'published',

    lede:
      'A methodology-week. Three new Lab findings expand the population-decline corpus into religious-community structural mechanism (Israel-Haredi), abstract refugia template (Form 4 cross-substrate), and substrate-symmetric refugia (the structural-cultural lens generalized beyond fertility). The tectonic-shifts model upgraded to v3 — categories (active / historical / emerging), depth fields (contrarian read, named positions, watch metrics, historical analogs, track record), 6 historical shifts authored at full depth as the analog library, and chart components added to detail pages. The publishing surface for weekly cadence (this brief) is itself the deliverable this week.',

    moved: [
      {
        shift: 'demographics',
        headline: 'Population-decline corpus expanded to 6 findings',
        body: 'The popdec wedge now carries Korea-Hungary (Form 2), Korea Lock-In (Form 5), Positive-Case Search (Form 3 falsification test), Israel-Haredi Floor (religious-community structural mechanism), Refugia Template (Form 4), and Substrate-Symmetric Refugia. The cross-citation network is dense enough that reading any one finding pulls a reader through the trilogy + the substrate generalization.',
      },
      {
        shift: 'demographics',
        headline: 'Israel-Haredi finding documents the structural-cultural positive case',
        body: 'Israel\'s sustained TFR ~2.85 is predicted ABOVE the 2030-2035 falsifier bar — but the wedge is religious-community structure (Haredi sub-community fertility ~6+, religious-Zionist ~3-4), not policy spend. The finding makes the structural-cultural mechanism explicit: where religious-community substrate exists, fertility floors form independent of policy.',
      },
      {
        finding: 'lab:finding/popdec/2026/substrate-symmetric-refugia/v1',
        headline: 'Substrate-symmetric refugia generalizes the lens',
        body: 'The structural-cultural framing extends beyond fertility. Where dense substrate (housing affordability, intergenerational caregiving, neighborhood child-density) exists, demographic resilience clusters. The finding identifies refugia patterns across high-fertility + high-trust + high-civic-engagement clusters that aren\'t reducible to ideology or policy.',
      },
    ],

    watchMetrics: [
      { metric: 'Lab finding count', value: '7', delta: '+3 this period', source: 'Lab corpus' },
      { metric: 'Pre-registered predictions on calendar', value: '~17+', delta: '+8 from new findings', source: '/lab/predictions' },
      { metric: 'Tracked tectonic shifts', value: '18', delta: '+8 from extension; +6 historical', source: '/strategic-playbook/tectonic-shifts' },
      { metric: 'Hungary 2024 period TFR', value: '1.38', delta: 'within prediction band [1.30, 1.50]', source: 'Hungarian KSH' },
      { metric: 'Korea 2024 period TFR', value: '0.75', delta: 'within prediction band [0.70, 0.90]', source: 'Statistics Korea' },
    ],

    predictionsOnDeck: [
      { findingHandle: 'lab:finding/popdec/2026/korea-hungary-divergence/v1',
        prediction: 'Hungary 2027 TFR in [1.30, 1.50]; central 1.40',
        resolutionDate: '2028 KSH release',
        daysOut: '~700' },
      { findingHandle: 'lab:finding/popdec/2026/korea-hungary-divergence/v1',
        prediction: 'Korea 2027 TFR in [0.70, 0.90]; central 0.78',
        resolutionDate: '2028 Statistics Korea release',
        daysOut: '~700' },
      { findingHandle: 'lab:finding/strategic/2026/asymmetric-underdog-inversion/v1',
        prediction: 'Indiana 2026 season (Cignetti year 3): 10+ wins, top-15 finish',
        resolutionDate: '2027-01',
        daysOut: '~240' },
    ],

    predictionsResolved: [
      // Empty for first brief — track record builds over time
    ],

    watchingNext: [
      'Eurostat Q1 fertility-rate release (early indicator for 2025 cohort trajectory across EU candidates).',
      'Vilnius Summit readouts on Defense Industrial Base capacity commitments — 155mm shell production rates and AUKUS submarine milestones.',
      'AI Boom: hyperscaler capex commentary in early-summer 10-Q releases — first signal of whether vertical-AI capex is cresting or accelerating.',
    ],

    citedFindings: [
      'lab:finding/popdec/2026/korea-hungary-divergence/v1',
      'lab:finding/popdec/2026/korea-lockin/v1',
      'lab:finding/popdec/2026/positive-case-search/v1',
      'lab:finding/popdec/2026/israel-haredi-floor/v1',
      'lab:finding/popdec/2026/refugia-template/v1',
      'lab:finding/popdec/2026/substrate-symmetric-refugia/v1',
      'lab:finding/strategic/2026/asymmetric-underdog-inversion/v1',
    ],

    citedShifts: [
      'demographics',
    ],
  },
];

export function getBrief(slug) {
  return BRIEFS.find((b) => b.slug === slug) || null;
}

export function listBriefs() {
  // Most recent first
  return [...BRIEFS].sort((a, b) => b.publishDate.localeCompare(a.publishDate));
}
